import { Spacer } from "@hackthenorth/north";
import React, { KeyboardEvent, useState } from "react";
import styled from "styled-components";

import { XMarkIcon } from "src/static/icons";
import {
  TextDescription,
  TextInputTitle,
} from "src/views/sponsor/perks/components/Perk/TextComponents";
export const TrackingCodeInput = ({
  title,
  codes,
  onChange,
  isReadOnly = false,
}: {
  title?: string | React.ReactNode;
  codes: string[];
  onChange: (codes: string[]) => void;
  isReadOnly?: boolean;
}) => {
  const [inputValue, setInputValue] = useState("");

  const addCode = (raw: string) => {
    const trimmed = raw.trim();
    // blocks codes that are empty and that already exist
    if (trimmed && !codes.includes(trimmed)) {
      onChange([...codes, trimmed]);
    }
  };

  const remove = (index: number) => {
    onChange(codes.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
      e.preventDefault();
      addCode(inputValue);
      setInputValue("");
    } else if (e.key === "Backspace" && !inputValue && codes.length > 0) {
      remove(codes.length - 1);
    }
  };

  return (
    <>
      {title && (
        <>
          <TextInputTitle>{title}</TextInputTitle>
          <Spacer height={8} />
          <TextDescription>
            Press Enter or comma to add a tracking code
          </TextDescription>
          <Spacer height={8} />
        </>
      )}
      <TagContainer>
        {codes.map((code, i) => (
          <Tag key={`${code}-${i}`}>
            <span>{code}</span>
            {!isReadOnly && (
              <RemoveButton
                type="button"
                onClick={() => remove(i)}
                aria-label={`Remove ${code}`}
              >
                <XMarkIcon width={10} height={10} />
              </RemoveButton>
            )}
          </Tag>
        ))}
        {!isReadOnly && (
          <CodeInput
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        )}
      </TagContainer>
    </>
  );
};

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  min-height: 42px;
  padding: 6px 10px;
  border: 2px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 6px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  font-size: 13px;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.globalConstants.color.textBody};
  }
`;

const CodeInput = styled.input`
  flex: 1;
  min-width: 180px;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
  padding: 2px 0;

  &::placeholder {
    color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  }
`;
